import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { t } from 'i18next'
import { Divider } from 'components/divider'
import { Field, Label, Switch } from '@headlessui/react'
import { useMutation, useQuery } from '@apollo/client'
import { GET_ROUTE_TYPE_LIST, GET_COUNTRY_LIST } from 'graphql/common/queries'
import { ADD_ROUTE_PRICE_DISCOUNT, UPDATE_ROUTE_PRICE_DISCOUNT } from 'graphql/common/mutations'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { NationalityDropdown } from 'components/custom/NationalityDropdown'
import Flatpickr from 'react-flatpickr'
import loadingImage from 'assets/images/boat-loading.gif'

export default function DiscountCodeForm() {
    const navigate = useNavigate()
    const { state } = useLocation()
    const discountDetails = state?.discountInfo
    const tabFrom = state?.currentTab

    // Query route types and countries
    const { data: routeTypeData, loading: routeTypeLoading } = useQuery(GET_ROUTE_TYPE_LIST)
    const { data: countryData, loading: countryLoading } = useQuery(GET_COUNTRY_LIST)

    // Combined loading state
    const isLoading = routeTypeLoading || countryLoading

    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    // get State variable value

    const [routePriceDiscount, setRoutePriceDiscount] = useState(null)
    const [mandatoryError, setMandatoryError] = useState(false)

    useEffect(() => {
        setRoutePriceDiscount({
            discount_code: discountDetails?.discount_code || '',
            discount_conditions: {
                age_to: discountDetails?.discount_conditions?.age_to
                    ? parseInt(discountDetails.discount_conditions.age_to)
                    : null,
                age_from:
                    discountDetails?.discount_conditions?.age_from >= 0
                        ? parseInt(discountDetails.discount_conditions.age_from)
                        : null,
                seat_type: discountDetails?.discount_conditions?.seat_type || null,
                nationality_code: discountDetails?.discount_conditions?.nationality_code || null,
                special_needs: discountDetails?.discount_conditions?.special_needs || null,
                route_type: discountDetails?.discount_conditions?.route_type || 'INL',
                country_code: discountDetails?.discount_conditions?.country_code || null,
            },
            discount_value: {
                type: discountDetails?.discount_value?.type || 'value',
                value: discountDetails?.discount_value?.value ? parseInt(discountDetails.discount_value.value) : '',
            },
            status_code: discountDetails?.status?.code || 'ACT',
            discount_desc: {
                en: discountDetails?.discount_desc?.en || '',
                ar: discountDetails?.discount_desc?.ar || discountDetails?.discount_desc?.en,
            },
            valid_from: discountDetails?.valid_from || null,
            valid_to: discountDetails?.valid_to || null,
        })
    }, [discountDetails])

    const handleBack = () => {
        navigate('/system-config', { state: { tabTo: tabFrom } })
    }
    // Mutation Add Discount
    const [addDiscountConfig, { loading: addLoading }] = useMutation(ADD_ROUTE_PRICE_DISCOUNT, {
        onCompleted: data => {
            if (data?.createRoutePriceDiscount.code === '200' && data?.createRoutePriceDiscount.success === true) {
                setShowSucDialog(true)
                setTimeout(() => {
                    setShowSucDialog(false)
                    navigate('/system-config')
                }, 500)
            } else {
                setDialogMessage(data?.createRoutePriceDiscount.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessage(error.message)
            setShowErrDialog(true)
        },
    })
    const [updateDiscountConfig, { loading: updateLoading }] = useMutation(UPDATE_ROUTE_PRICE_DISCOUNT, {
        onCompleted: data => {
            if (data?.updateRoutePriceDiscount.code === '200' && data?.updateRoutePriceDiscount.success === true) {
                setShowSucDialog(true)
                setTimeout(() => {
                    setShowSucDialog(false)
                    navigate('/system-config')
                }, 500)
            } else {
                setDialogMessage(data?.updateRoutePriceDiscount.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessage(error.message)
            setShowErrDialog(true)
        },
    })
    const handleSaveDiscountConfig = values => {
        // mandatory fields validation
        if (
            !routePriceDiscount?.discount_code ||
            !routePriceDiscount?.discount_value?.value ||
            !routePriceDiscount?.discount_desc?.en ||
            !routePriceDiscount?.discount_desc?.ar ||
            !routePriceDiscount?.discount_conditions?.route_type
        ) {
            setMandatoryError(true)
            return
        }
        const formattedData = {
            routePriceDiscount: {
                ...routePriceDiscount,
                // For new entries, always set ACT. For edit, use current status
                status_code: discountDetails ? routePriceDiscount.status_code : 'ACT',
            },
        }

        // change routePriceDiscount.discount_value.value to number
        formattedData.routePriceDiscount.discount_value.value = Number(
            formattedData.routePriceDiscount.discount_value.value
        )
        if (discountDetails && discountDetails.discount_id) {
            formattedData.discountId = discountDetails.discount_id || null
            updateDiscountConfig({
                variables: formattedData,
            })
        } else {
            addDiscountConfig({
                variables: formattedData,
            })
        }
    }
    const handleDialogClose = () => {
        setShowErrDialog(false)
        setShowSucDialog(false)
    }

    return (
        <>
            {isLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16 w-full">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="mb-8 flex justify-normal items-center">
                <button
                    onClick={handleBack}
                    type="button"
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <h3 className="text-lg font-bold leading-7 text-primary ps-2.5 pb-1">
                    {discountDetails ? t('Edit Discount') : t('Add Discount')}
                </h3>
            </div>

            <div className="py-5 mt-2">
                {/* Route Condition Section */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Route Condition')}</h2>
                        <p className="mt-1 text-sm leading-6 text-gray-600">
                            {t('Select route type and country for the discount')}
                        </p>
                    </div>
                    <div className="grid max-w-xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <label htmlFor="route_type" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Route Type')}
                                <span className="text-red">*</span>
                            </label>
                            <div className="mt-2">
                                <select
                                    id="route_type"
                                    name="route_type"
                                    value={routePriceDiscount?.discount_conditions?.route_type}
                                    onChange={e => {
                                        setRoutePriceDiscount({
                                            ...routePriceDiscount,
                                            discount_conditions: {
                                                ...routePriceDiscount.discount_conditions,
                                                route_type: e.target.value,
                                                country_code: null, // Reset country when route type changes
                                            },
                                        })
                                        setMandatoryError(false)
                                    }}
                                    className={`block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ${
                                        mandatoryError && !routePriceDiscount?.discount_conditions?.route_type
                                            ? 'ring-red-500 focus:ring-red-500'
                                            : 'ring-gray-300 focus:ring-primary'
                                    } focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                                >
                                    <option value="INL">International</option>
                                    <option value="DMC">Domestic</option>
                                </select>
                                {mandatoryError && !routePriceDiscount?.discount_conditions?.route_type && (
                                    <p className="text-red-500 text-xs mt-1">{t('Route type is required')}</p>
                                )}
                            </div>
                        </div>
                        {routePriceDiscount?.discount_conditions?.route_type === 'DMC' && (
                            <div className="sm:col-span-full">
                                <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                    <div className="px-4 py-3 sm:p-3">
                                        <Field className="flex items-center justify-between">
                                            <span className="flex flex-grow flex-col">
                                                <Label
                                                    as="span"
                                                    passive
                                                    className="text-sm font-medium leading-6 text-gray-900"
                                                >
                                                    {t('Country')}
                                                </Label>
                                            </span>
                                            <Switch
                                                onChange={checked => {
                                                    setRoutePriceDiscount({
                                                        ...routePriceDiscount,
                                                        discount_conditions: {
                                                            ...routePriceDiscount.discount_conditions,
                                                            country_code: checked
                                                                ? routePriceDiscount?.discount_conditions?.country_code || 'BH'
                                                                : null,
                                                        },
                                                    })
                                                }}
                                                checked={routePriceDiscount?.discount_conditions?.country_code !== null}
                                                className={`${
                                                    routePriceDiscount?.discount_conditions?.country_code !== null
                                                        ? 'bg-primary'
                                                        : 'bg-gray-200'
                                                } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                            >
                                                <span
                                                    aria-hidden="true"
                                                    className={`${
                                                        routePriceDiscount?.discount_conditions?.country_code !== null
                                                            ? 'translate-x-5'
                                                            : 'translate-x-0'
                                                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                                />
                                            </Switch>
                                        </Field>
                                    </div>
                                    {routePriceDiscount?.discount_conditions?.country_code !== null && (
                                        <div className="grid grid-cols-1 p-4 w-[50%]">
                                            <select
                                                id="country_code"
                                                name="country_code"
                                                value={routePriceDiscount?.discount_conditions?.country_code}
                                                onChange={e => {
                                                    setRoutePriceDiscount({
                                                        ...routePriceDiscount,
                                                        discount_conditions: {
                                                            ...routePriceDiscount.discount_conditions,
                                                            country_code: e.target.value,
                                                        },
                                                    })
                                                }}
                                                className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                                            >
                                                {countryData?.country?.map(country => (
                                                    <option key={country.alpha_iso2} value={country.alpha_iso2}>
                                                        {country.country}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Discount Condition Section */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3 mt-8">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Discount Condition')}</h2>
                        <p className="mt-1 text-sm leading-6 text-gray-600">
                            {t('Personal Information of the user for contact purpose.')}
                        </p>
                    </div>
                    <div className="grid max-w-xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                <div className="px-4 py-3 sm:p-3">
                                    <Field className="flex items-center justify-between">
                                        <span className="flex flex-grow flex-col">
                                            <Label
                                                as="span"
                                                passive
                                                className="text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('Seat Type')}
                                            </Label>
                                        </span>
                                        <Switch
                                            onChange={checked => {
                                                setRoutePriceDiscount({
                                                    ...routePriceDiscount,
                                                    discount_conditions: {
                                                        ...routePriceDiscount.discount_conditions,
                                                        seat_type: checked
                                                            ? routePriceDiscount?.discount_conditions?.seat_type ||
                                                              'STD'
                                                            : null,
                                                    },
                                                })
                                            }}
                                            checked={routePriceDiscount?.discount_conditions?.seat_type !== null}
                                            className={`${
                                                routePriceDiscount?.discount_conditions?.seat_type !== null
                                                    ? 'bg-primary'
                                                    : 'bg-gray-200'
                                            } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={`${
                                                    routePriceDiscount?.discount_conditions?.seat_type !== null
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0'
                                                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                            />
                                        </Switch>
                                    </Field>
                                </div>
                                <div className=" grid grid-cols-1 p-4 w-[50%]">
                                    {routePriceDiscount?.discount_conditions?.seat_type !== null && (
                                        <select
                                            onChange={e => {
                                                setRoutePriceDiscount({
                                                    ...routePriceDiscount,
                                                    discount_conditions: {
                                                        ...routePriceDiscount.discount_conditions,
                                                        seat_type: e.target.value,
                                                    },
                                                })
                                            }}
                                            id="seat_type"
                                            name="seat_type"
                                            value={routePriceDiscount?.discount_conditions?.seat_type}
                                            className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                                        >
                                            <option value="STD">Standard</option>
                                            <option value="VIP">VIP</option>
                                            <option value="BOTH">Both</option>
                                        </select>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                <div className="px-4 py-3 sm:p-3">
                                    <Field className="flex items-center justify-between">
                                        <span className="flex flex-grow flex-col">
                                            <Label
                                                as="span"
                                                passive
                                                className="text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('Nationality')}
                                            </Label>
                                        </span>
                                        <Switch
                                            onChange={checked => {
                                                setRoutePriceDiscount({
                                                    ...routePriceDiscount,
                                                    discount_conditions: {
                                                        ...routePriceDiscount.discount_conditions,
                                                        nationality_code: checked
                                                            ? routePriceDiscount?.discount_conditions
                                                                  ?.nationality_code || 'BH'
                                                            : null,
                                                    },
                                                })
                                            }}
                                            checked={routePriceDiscount?.discount_conditions?.nationality_code !== null}
                                            className={`${
                                                routePriceDiscount?.discount_conditions?.nationality_code !== null
                                                    ? 'bg-primary'
                                                    : 'bg-gray-200'
                                            } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={`${
                                                    routePriceDiscount?.discount_conditions?.nationality_code !== null
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0'
                                                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                            />
                                        </Switch>
                                    </Field>
                                </div>
                                <div className=" grid grid-cols-1 p-4 w-[50%]">
                                    {routePriceDiscount?.discount_conditions?.nationality_code !== null && (
                                        <NationalityDropdown
                                            fields={{
                                                name: `nationality_code`,
                                                disabled: false,
                                                onChange: e => {
                                                    setRoutePriceDiscount({
                                                        ...routePriceDiscount,
                                                        discount_conditions: {
                                                            ...routePriceDiscount.discount_conditions,
                                                            nationality_code: e.target.value,
                                                        },
                                                    })
                                                },
                                                value: routePriceDiscount?.discount_conditions?.nationality_code,
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                <div className="px-4 py-3 sm:p-3">
                                    <Field className="flex items-center justify-between">
                                        <span className="flex flex-grow flex-col">
                                            <Label
                                                as="span"
                                                passive
                                                className="text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t(' Age range')}
                                            </Label>
                                        </span>
                                        <Switch
                                            onChange={checked => {
                                                setRoutePriceDiscount({
                                                    ...routePriceDiscount,
                                                    discount_conditions: {
                                                        ...routePriceDiscount.discount_conditions,
                                                        age_from: checked
                                                            ? (routePriceDiscount?.discount_conditions?.age_from ?? 0)
                                                            : null,
                                                        age_to: checked
                                                            ? (routePriceDiscount?.discount_conditions?.age_to ?? 0)
                                                            : null,
                                                    },
                                                })
                                            }}
                                            checked={
                                                routePriceDiscount?.discount_conditions?.age_from !== null &&
                                                routePriceDiscount?.discount_conditions?.age_to !== null &&
                                                (routePriceDiscount?.discount_conditions?.age_from !== '' || 
                                                 routePriceDiscount?.discount_conditions?.age_to !== '')
                                            }
                                            className={`${
                                                routePriceDiscount?.discount_conditions?.age_from !== null &&
                                                routePriceDiscount?.discount_conditions?.age_to !== null
                                                    ? 'bg-primary'
                                                    : 'bg-gray-200'
                                            } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={`${
                                                    routePriceDiscount?.discount_conditions?.age_from !== null &&
                                                    routePriceDiscount?.discount_conditions?.age_to !== null
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0'
                                                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                            />
                                        </Switch>
                                    </Field>
                                </div>
                                <div className="flex w-[50%] p-4 ">
                                    {routePriceDiscount?.discount_conditions?.age_from !== null &&
                                        routePriceDiscount?.discount_conditions?.age_to !== null && (
                                        <>
                                            <div className="text-sm/6">
                                                <div className=" grid grid-cols-1">
                                                    <input
                                                        id="from_age"
                                                        name="from_age"
                                                        type="number"
                                                        value={routePriceDiscount?.discount_conditions?.age_from}
                                                        onChange={e => {
                                                            setRoutePriceDiscount({
                                                                ...routePriceDiscount,
                                                                discount_conditions: {
                                                                    ...routePriceDiscount.discount_conditions,
                                                                    age_from: parseInt(e.target.value),
                                                                },
                                                            })
                                                        }}
                                                        placeholder="From Age"
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-sm/6">
                                                <div className=" grid grid-cols-1">
                                                    <input
                                                        id="to_age"
                                                        name="to_age"
                                                        min={routePriceDiscount?.discount_conditions?.age_from}
                                                        value={routePriceDiscount?.discount_conditions?.age_to}
                                                        onChange={e => {
                                                            setRoutePriceDiscount({
                                                                ...routePriceDiscount,
                                                                discount_conditions: {
                                                                    ...routePriceDiscount.discount_conditions,
                                                                    age_to: parseInt(e.target.value),
                                                                },
                                                            })
                                                        }}
                                                        type="number"
                                                        placeholder="To Age"
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                <div className="px-4 py-3 sm:p-3">
                                    <Field className="flex items-center justify-between">
                                        <span className="flex flex-grow flex-col">
                                            <Label
                                                as="span"
                                                passive
                                                className="text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('Special Needs')}
                                            </Label>
                                        </span>
                                        <Switch
                                            onChange={checked => {
                                                setRoutePriceDiscount({
                                                    ...routePriceDiscount,
                                                    discount_conditions: {
                                                        ...routePriceDiscount.discount_conditions,
                                                        special_needs: checked ? true : null,
                                                    },
                                                })
                                            }}
                                            checked={routePriceDiscount?.discount_conditions?.special_needs === true}
                                            className={`${
                                                routePriceDiscount?.discount_conditions?.special_needs === true
                                                    ? 'bg-primary'
                                                    : 'bg-gray-200'
                                            } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={`${
                                                    routePriceDiscount?.discount_conditions?.special_needs === true
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0'
                                                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                            />
                                        </Switch>
                                    </Field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="space-y-6 mt-3">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Discount Value')}</h2>
                        <p className="mt-1 text-sm leading-6 text-gray-600">
                            {t('Enter discount value by fixed price or percentage(%).')}
                        </p>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                            <fieldset>
                                <div className="mt-4 space-y-6">
                                    <div className="flex gap-3">
                                        <div className="text-sm/6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                {t('Discount Code')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <div className="mt-1 grid grid-cols-2">
                                                <input
                                                    id="discount_code"
                                                    name="discount_code"
                                                    type="text"
                                                    value={routePriceDiscount?.discount_code}
                                                    onChange={e => {
                                                        setRoutePriceDiscount({
                                                            ...routePriceDiscount,
                                                            discount_code: e.target.value,
                                                        })
                                                    }}
                                                    onBlur={e => {
                                                        setMandatoryError(e.target.value === '')
                                                    }}
                                                    placeholder="Enter here"
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        <div className="col-span-full">
                            <fieldset>
                                <div className="mt-2 space-y-6">
                                    <div className="flex gap-3">
                                        <div className="text-sm/6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                {t('Discount Value By')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <div className="mt-1 grid grid-cols-2">
                                                <select
                                                    id="discount_value_type"
                                                    name="discount_value_type"
                                                    value={routePriceDiscount?.discount_value?.type}
                                                    onChange={e => {
                                                        setRoutePriceDiscount({
                                                            ...routePriceDiscount,
                                                            discount_value: {
                                                                ...routePriceDiscount.discount_value,
                                                                type: e.target.value,
                                                            },
                                                        })
                                                    }}
                                                    className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                                                >
                                                    <option value="percentage">Percentage %</option>
                                                    <option value="value">Fixed</option>
                                                </select>
                                                <input
                                                    id="discount_value"
                                                    name="discount_value"
                                                    type="number"
                                                    value={routePriceDiscount?.discount_value?.value}
                                                    onChange={e => {
                                                        setRoutePriceDiscount({
                                                            ...routePriceDiscount,
                                                            discount_value: {
                                                                ...routePriceDiscount.discount_value,
                                                                value: e.target.value,
                                                            },
                                                        })
                                                    }}
                                                    onBlur={e => {
                                                        setMandatoryError(e.target.value === '')
                                                    }}
                                                    placeholder="Enter here"
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        <div className="col-span-full">
                            <fieldset>
                                <div className="mt-2 space-y-6">
                                    <div className="flex gap-3">
                                        <div className="text-sm/6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                {t('Discount Validity')}
                                            </label>
                                            <div className="mt-1 grid grid-cols-2">
                                                <Flatpickr
                                                    placeholder="Valid From"
                                                    value={routePriceDiscount?.valid_from}
                                                    onChange={date =>
                                                        setRoutePriceDiscount({
                                                            ...routePriceDiscount,
                                                            valid_from: date[0]
                                                                ? date[0]
                                                                      .toLocaleDateString('en-CA')
                                                                      .replace(/\//g, '-')
                                                                : '',
                                                        })
                                                    }
                                                    options={{
                                                        altInput: true,
                                                        altFormat: 'Y/m/d',
                                                        dateFormat: 'Y/m/d',
                                                        value: routePriceDiscount?.valid_from,
                                                        defaultDate: routePriceDiscount?.valid_from || new Date(),
                                                        // maxDate: routePriceDiscount?.valid_to,
                                                    }}
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                />
                                                <Flatpickr
                                                    placeholder="Valid To"
                                                    value={routePriceDiscount?.valid_to}
                                                    onChange={date =>
                                                        setRoutePriceDiscount({
                                                            ...routePriceDiscount,
                                                            valid_to: date[0]
                                                                ? date[0]
                                                                      .toLocaleDateString('en-CA')
                                                                      .replace(/\//g, '-')
                                                                : '',
                                                        })
                                                    }
                                                    options={{
                                                        altInput: true,
                                                        altFormat: 'Y/m/d',
                                                        dateFormat: 'Y/m/d',
                                                        value: routePriceDiscount?.valid_to,
                                                        defaultDate: routePriceDiscount?.valid_to,
                                                        minDate: routePriceDiscount?.valid_from,
                                                    }}
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        <div className="col-span-full">
                            <fieldset>
                                <div className="mt-2 space-y-6">
                                    <div className="flex gap-3 w-full">
                                        <div className="text-sm/6 w-full">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                {t('Discount description (English)')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <textarea
                                                id="description_en"
                                                name="description_en"
                                                value={routePriceDiscount?.discount_desc?.en}
                                                onChange={e => {
                                                    setRoutePriceDiscount({
                                                        ...routePriceDiscount,
                                                        discount_desc: {
                                                            ...routePriceDiscount.discount_desc,
                                                            en: e.target.value,
                                                        },
                                                    })
                                                }}
                                                onBlur={e => {
                                                    setMandatoryError(e.target.value === '')
                                                }}
                                                rows="3"
                                                className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        <div className="col-span-full" style={{ direction: 'rtl' }}>
                            <fieldset>
                                <div className="mt-2 space-y-6">
                                    <div className="flex gap-3 w-full">
                                        <div className="text-sm/6 w-full">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                {t('Discount description (Arabic)')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <textarea
                                                id="description_ar"
                                                name="description_ar"
                                                value={routePriceDiscount?.discount_desc?.ar}
                                                onChange={e => {
                                                    setRoutePriceDiscount({
                                                        ...routePriceDiscount,
                                                        discount_desc: {
                                                            ...routePriceDiscount.discount_desc,
                                                            ar: e.target.value,
                                                        },
                                                    })
                                                }}
                                                onBlur={e => {
                                                    setMandatoryError(e.target.value === '')
                                                }}
                                                rows="3"
                                                className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        {discountDetails && (
                            <div className="sm:col-span-full">
                                <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                    <div className="px-4 py-3 sm:p-3">
                                        <Field className="flex items-center justify-between">
                                            <span className="flex flex-grow flex-col">
                                                <Label
                                                    as="span"
                                                    passive
                                                    className="text-sm font-medium leading-6 text-gray-900"
                                                >
                                                    {t('Status')}
                                                </Label>
                                            </span>
                                            <Switch
                                                onChange={checked => {
                                                    setRoutePriceDiscount(prev => ({
                                                        ...prev,
                                                        status_code: checked ? 'ACT' : 'INA',
                                                    }))
                                                }}
                                                checked={routePriceDiscount?.status_code === 'ACT'}
                                                className={`${
                                                    routePriceDiscount?.status_code === 'ACT'
                                                        ? 'bg-primary'
                                                        : 'bg-gray-200'
                                                } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                            >
                                                <span
                                                    aria-hidden="true"
                                                    className={`${
                                                        routePriceDiscount?.status_code === 'ACT'
                                                            ? 'translate-x-5'
                                                            : 'translate-x-0'
                                                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                                />
                                            </Switch>
                                        </Field>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Divider />

            <div className="mt-6 flex items-center justify-end gap-x-3">
                {mandatoryError && <p className="text-error text-xs mt-1">{t('Please fill mandatory fields')}</p>}
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                    {t('Cancel')}
                </button>
                <button
                    type="submit"
                    onClick={handleSaveDiscountConfig}
                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    {t('Save')}
                </button>
            </div>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={dialogMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={dialogMessage} onClose={handleDialogClose} />
            )}
            {updateLoading ||
                (addLoading && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                    </div>
                ))}
                </>
            )}
        </>
    )
}
